import axiosInstance from '../lib/axios';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/actions/authActions';
import useAuth from './useAuth';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

const useAxiosPrivate = () => {
	const { auth } = useAuth();
	const dispatch = useDispatch();

	useEffect(() => {
		// Configure NProgress
		NProgress.configure({ 
			showSpinner: true,
			parent: 'body'
		});

		// Custom styling for NProgress
		const style = document.createElement('style');
		style.textContent = `
			#nprogress {
				pointer-events: none;
			}
			#nprogress .bar { display: none !important; }
			#nprogress .peg { display: none !important; }
			#nprogress::before {
				content: '';
				position: fixed;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				background: rgba(255, 255, 255, 0.7);
				z-index: 99998;
				pointer-events: all;
			}
			#nprogress .spinner {
				display: flex;
				align-items: center;
				justify-content: center;
				position: fixed;
				z-index: 99999;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				margin: auto;
			}
			#nprogress .spinner-icon {
				width: 40px;
				height: 40px;
				box-sizing: border-box;
				border: solid 3px transparent;
				border-top-color: #0F9BB0;
				border-left-color: #0F9BB0;
				border-radius: 50%;
				animation: nprogress-spinner 400ms linear infinite;
			}
			@keyframes nprogress-spinner {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);

		// Track number of ongoing requests
		let requestCount = 0;
		let nprogressStartTime = null;
		const MIN_PROGRESS_TIME = 300; // ms
		const GRACE_PERIOD = 200; // ms
		let graceTimeout = null;

		const clearGraceTimeout = () => {
			if (graceTimeout) {
				clearTimeout(graceTimeout);
				graceTimeout = null;
			}
		};

		const updateProgress = () => {
			requestCount--;
			if (requestCount <= 0) {
				requestCount = 0;
				const now = Date.now();
				const elapsed = nprogressStartTime ? now - nprogressStartTime : 0;
				clearGraceTimeout();
				graceTimeout = setTimeout(() => {
					if (requestCount === 0) {
						if (elapsed < MIN_PROGRESS_TIME) {
							setTimeout(() => {
								NProgress.done();
								nprogressStartTime = null;
							}, MIN_PROGRESS_TIME - elapsed);
						} else {
							NProgress.done();
							nprogressStartTime = null;
						}
					}
				}, GRACE_PERIOD);
			}
		};

		const requestIntercept = axiosInstance.interceptors.request.use(
			config => {
				clearGraceTimeout();
				requestCount++;
				if (requestCount === 1) {
					NProgress.start();
					nprogressStartTime = Date.now();
				}
				const token = auth?.accessToken || sessionStorage.getItem('accessToken');
				// attach Authorization header if not present
				if (!config.headers['Authorization'] && token) {
					config.headers['Authorization'] = `Bearer ${token}`;
				}
				return config;
			},
			error => {
				updateProgress();
				return Promise.reject(error);
			}
		);

		const responseIntercept = axiosInstance.interceptors.response.use(
			function (response) {
				updateProgress();
				return response;
			},
			function (error) {
				updateProgress();
				const isUnauthorized = error?.response?.status === 401;
				const isNotLoginPage = window.location.pathname !== '/';
				const isNotChangePassword = !error?.config?.url?.includes('user/change_password');

				if (isUnauthorized && isNotLoginPage && isNotChangePassword) {
					dispatch(logout());
					window.location.href = '/';
				}

				return Promise.reject(error);
			}
		);

		return () => {
			axiosInstance.interceptors.request.eject(requestIntercept);
			axiosInstance.interceptors.response.eject(responseIntercept);
			// Reset progress and counter on cleanup
			requestCount = 0;
			clearGraceTimeout();
			NProgress.done();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth]);

	return axiosInstance;
};

export default useAxiosPrivate;
